// Components==============
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/UI/Button/Button';
import Input from 'components/UI/Form/Input';
import Textarea from 'components/UI/Form/Textarea';
import SkillModal from 'components/UI/SkillModal/SkillModal';
import SparkGoals from 'components/UI/SparkGoals/SparkGoals';
import { AnimatePresence } from 'framer-motion';
import { EditableProcessEntity } from 'hooks/useProcessGroups';
import { useTranslate } from 'hooks/useTranslate';
import { ProjectDetailsContext } from 'pages/Workspace/ProjectDetails/ProjectDetails';
import React, { createContext, useContext, useState } from 'react';
import { ProjectProcessContext } from '../../ProjectProcess';
import './EndPoint.module.scss';
// =========================

export type UserSparksContextType = {
  handleAddSparkGoal: (sparks: SparkEntity[]) => void;
  handleDeleteSparkGoal: (id: number) => void;
};

export const UserCon = createContext({} as UserSparksContextType);

export default function Edit({
  description,
  process,
}: {
  description: EndPointType;
  process?: EditableProcessEntity;
}) {
  const { setProcessGroups, setSparks, sparks } = useContext(
    ProjectDetailsContext
  );

  const { groupedSparks } = useContext(ProjectProcessContext);

  const translate = useTranslate();
  const [toggleFocus, setToggleFocus] = useState(-1);
  const [toggleSubFocus, setToggleSubFocus] = useState(-1);

  const [skillModalIsOpen, setSkillModalIsOpen] = useState(false);

  const stateChangeHelper = (newVal: EndPointType) =>
    setProcessGroups((prev) =>
      prev.map((group) => {
        if (
          group.id === process?.id &&
          group.process_type === process.process_type
        ) {
          return {
            ...group,
            description: {
              ...(group.description as EndPointType),
              ...newVal,
            },
          };
        }
        return group;
      })
    );

  function handleChangeNotes(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) {
    stateChangeHelper({
      note: e.target.value,
    });
  }

  function handleAddSparkGoal(newSparks: SparkEntity[]) {
    const uniqueSparks = sparks.filter(
      (spark) => !newSparks.some((s) => s.id === spark.id)
    );

    setSparks((prevSparks) => {
      const updatedSparks = [
        ...prevSparks,
        ...uniqueSparks.map((spark) => ({ ...spark, done: false })),
      ];
      return updatedSparks;
    });
  }

  function handleDeleteSparkGoal(id: number) {
    setSparks((prevSparks) => {
      const updatedSparks = prevSparks.filter((spark) => spark.id !== id);
      return updatedSparks;
    });
  }

  function handleAddDeliveryGoal(value: string) {
    const newId = description?.delivery_goals
      ? description?.delivery_goals?.[description?.delivery_goals.length - 1]
          ?.id + 1
      : NaN;

    const id = newId || 1;

    if (!value) return;

    setToggleFocus(id);

    if (description?.delivery_goals?.length)
      return stateChangeHelper({
        delivery_goals: [
          ...description?.delivery_goals,
          { id, value, done: false },
        ],
      });

    stateChangeHelper({
      delivery_goals: [{ id, value, done: false }],
    });
  }

  function handleDeliveryGoalsChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    id: number
  ) {
    if (!e.target.value) {
      setToggleFocus(0);

      return stateChangeHelper({
        delivery_goals: description?.delivery_goals?.filter((d) => d.id !== id),
      });
    }

    stateChangeHelper({
      delivery_goals: description?.delivery_goals?.map((d) =>
        d.id === id ? { ...d, value: e.target.value } : d
      ),
    });
  }

  function handleDeleteDeliveryGoal(id: number) {
    return stateChangeHelper({
      delivery_goals: description?.delivery_goals?.filter((d) => d.id !== id),
    });
  }

  function handleAddSubGoal(value: string) {
    const newId = description?.sub_goals
      ? description?.sub_goals?.[description?.sub_goals.length - 1]?.id + 1
      : NaN;

    const id = newId || 1;

    if (!value) return;

    setToggleSubFocus(id);

    if (description?.sub_goals?.length)
      return stateChangeHelper({
        sub_goals: [...description?.sub_goals, { id, value, done: false }],
      });

    stateChangeHelper({
      sub_goals: [{ id, value, done: false }],
    });
  }

  function handleSubGoalChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    id: number
  ) {
    if (!e.target.value) {
      setToggleSubFocus(0);

      return stateChangeHelper({
        sub_goals: description?.sub_goals?.filter((d) => d.id !== id),
      });
    }

    stateChangeHelper({
      sub_goals: description?.sub_goals?.map((d) =>
        d.id === id ? { ...d, value: e.target.value } : d
      ),
    });
  }

  function handleDeleteSubGoal(id: number) {
    return stateChangeHelper({
      sub_goals: description?.sub_goals?.filter((d) => d.id !== id),
    });
  }

  const contextValues = {
    handleAddSparkGoal,
    handleDeleteSparkGoal,
  };

  return (
    <UserCon.Provider value={contextValues}>
      <div styleName="grid">
        <div>
          <h3 styleName="grey-h3-header">
            {translate('general.terms.delivery-goals')} <span>*</span>
          </h3>
          <div styleName=" sub-grid">
            {description?.delivery_goals?.map((d, i) => (
              <Input
                ext_formValue={d.value}
                ext_onChange={(e) => handleDeliveryGoalsChange(e, d.id)}
                onKeyDown={(e) => e.key === 'Enter' && setToggleFocus(0)}
                name="delivery_goal"
                placeholder={translate('general.terms.delivery-goal')}
                handleDelete={() => handleDeleteDeliveryGoal(d.id)}
                key={i}
                focusEffect={d.id === toggleFocus}
              />
            ))}
            <div styleName="new">
              <Input
                name="new_delivery_goal"
                ext_onChange={(e) => {
                  if (e.target.value) handleAddDeliveryGoal(e.target.value);
                }}
                placeholder={translate('general.terms.add-delivery-goal')}
                focusEffect={toggleFocus === 0}
              />
            </div>
          </div>
        </div>
        <div>
          <h3 styleName="grey-h3-header">
            {translate('general.terms.sub-goals')}
          </h3>
          <div styleName=" sub-grid">
            {description?.sub_goals?.map((g, i) => (
              <Input
                ext_formValue={g.value}
                ext_onChange={(e) => handleSubGoalChange(e, g.id)}
                onKeyDown={(e) => e.key === 'Enter' && setToggleSubFocus(0)}
                name="sub"
                placeholder={translate('general.terms.sub-goal')}
                key={i}
                handleDelete={() => handleDeleteSubGoal(g.id)}
                focusEffect={g.id === toggleSubFocus}
              />
            ))}
            <div styleName="new">
              <Input
                name="new_delivery_goal"
                ext_onChange={(e) => {
                  if (e.target.value) handleAddSubGoal(e.target.value);
                }}
                placeholder={translate('general.terms.add-sub-goal')}
                focusEffect={toggleSubFocus === 0}
              />
            </div>
          </div>
        </div>
        <div>
          <h3 styleName="spark-header">
            {translate('general.terms.spark-goals')}
          </h3>
          <p styleName="spark-terms">
            Met Sparks stel je persoonlijke leerdoelen in voor dit project.
          </p>
          <div styleName=" sub-grid">
            {Object.keys(groupedSparks!).map((skillId) => (
              <SparkGoals
                key={skillId}
                sparks={groupedSparks![skillId].sparks}
                handleDelete={handleDeleteSparkGoal}
              />
            ))}
            <div styleName="new">
              <Button
                color="purple-light"
                onClick={() => setSkillModalIsOpen(true)}
              >
                Sparks toevoegen
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            </div>
          </div>
        </div>
        <div>
          <h3 styleName="grey-h3-header">{translate('general.terms.notes')}</h3>
          <Textarea
            ext_formValue={description?.note}
            ext_onChange={handleChangeNotes}
            name="delivery_goals"
            placeholder={translate('general.terms.notes')}
          />
        </div>
      </div>
      <AnimatePresence>
        {!!skillModalIsOpen && (
          <SkillModal setSkillModalIsOpen={setSkillModalIsOpen} />
        )}
      </AnimatePresence>
    </UserCon.Provider>
  );
}
